import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import useUserInfo from "../../../Hooks/useUserInfo";
import { useAudio } from "../../../Providers/AudioProvider";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import Task from "../Task";
import "../matchmeme/matchmeme.css";
import { FaQuestionCircle } from "react-icons/fa";

const RacingContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #000;
  position: relative;
  pointer-events: fill;
`;

const GameFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  overflow: hidden;
`;

const CancelButton = styled.img`
  position: absolute;
  top: 45px;
  width: 30px;
  height: 30px;
  z-index: 10;
  cursor: pointer;
  left: 13px;
`;

const InstructionButton = styled.div`
  position: absolute;
  top: 45px;
  width: 30px;
  height: 30px;
  z-index: 10;
  cursor: pointer;
  right: 13px;
  color: #03bb14;
`;

const TestButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 20px;
  padding: 10px 15px;
  background: linear-gradient(180deg, #4d4dff 0%, #0000cc 100%);
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  z-index: 10;
  cursor: pointer;
  display: ${(props) => (props.visible ? "block" : "none")};
`;

const Racing = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState("");
  const [gameStarted, setGameStarted] = useState(false);
  const [earnedTokens, setEarnedTokens] = useState(0);
  const [playerStage, setPlayerStage] = useState(1);
  const [gameEnded, setGameEnded] = useState(false);
  const [showTestButton, setShowTestButton] = useState(
    process.env.NODE_ENV === "development"
  );
  const [showInstructions, setShowInstructions] = useState(false);
  const iframeRef = useRef(null);

  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const playClick = useAudio();
  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;

  useEffect(() => {
    logPageView("Racing Game", "Racing Game");

    // Reset game state in localStorage when component mounts
    localStorage.setItem("DW3", "0");
    localStorage.removeItem("DW4");
  }, []);

  // Function to reset the game state
  const resetGameState = () => {
    // Reset local state
    setPlayerStage(1);
    setGameEnded(false);
    setGameStarted(false);

    // Reset localStorage values
    localStorage.setItem("DW3", "0");
    localStorage.removeItem("DW4");

    // Send message to iframe to reset game
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: "RESET_RACING_GAME",
          action: "RESET_GAME",
        },
        "*"
      );

      // Reload the iframe to ensure a fresh game state
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: Task,
      currentComponentText: "Task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  // This is the main API calling function that needs to work
  const handleApiResponse = async (data) => {
    if (gameEnded) return; // Prevent multiple API calls
    try {
      setGameEnded(true); // Mark game as ended to prevent duplicate calls
      // Get stage and tokens from the game data
      const stage = data.playerStage || 1;
      // Only award tokens if player crossed stage 1 (reached stage 2 or higher)
      // When playerStage is 2, they completed stage 1, so award 500 tokens
      // When playerStage is 3, they completed stage 2, so award 1000 tokens
      const stageTokens = stage > 1 ? (stage - 1) * 500 : 0;
      console.log("Calling API with stage:", stage, "tokens:", stageTokens);
      // Set state for display
      setPlayerStage(stage);
      setEarnedTokens(stageTokens);
      // Only call API if game points are greater than 0
      if (stageTokens > 0) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: true,
        }));
        // Prepare API data
        const apiData = {
          gamePoints: stageTokens.toString(),
          gameType: "Racing",
          chancesToPlayed: 0,
        };
        // Call the API
        const res = await userGameRewards(apiData);
        console.log("API response:", res);
        // Handle API response
        if (res) {
          updateUserInfo((prev) => ({
            ...prev,
            isLoading: false,
          }));
          updatewatchScreenInfo((prev) => ({
            ...prev,
            LoginMessage: res?.message,
          }));
        } else {
          updateUserInfo((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      }
      // Determine popup type based on player win status and stage
      setPopupType(stage > 1 ? "win" : "gameover");
      // Show the popup
      setShowPopup(true);
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  // Handle test button click for development
  // const handleTestClick = () => {
  //   handleApiResponse({

  //     stageTokens: 1000,
  //     playerWin: true
  //   });
  // };

  // Listen for game events from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Make sure the message is from our game
      if (event.data && event.data.type === "RACING_GAME_EVENT") {
        if (event.data.action === "GAME_STARTED") {
          setGameStarted(true);
          setPlayerStage(1); // Reset stage when game starts
          setGameEnded(false); // Reset game ended state

          // Reset localStorage values when game starts
          localStorage.setItem("DW3", "0");
          localStorage.removeItem("DW4");
        } else if (event.data.action === "GAME_OVER" && !gameEnded) {
          console.log("Game ended with data:", event.data);
          handleApiResponse(event.data);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [gameEnded]);

  const closePopUp = () => {
    setShowPopup(false);
    setPlayerStage(1);
    setGameEnded(false);

    // Reset localStorage values when popup is closed
    localStorage.setItem("DW3", "0");
    localStorage.removeItem("DW4");

    // Reset the game completely
    resetGameState();
  };

  const toggleInstructions = () => {
    playClick();
    setShowInstructions(!showInstructions);
  };

  return (
    <RacingContainer>
      <CancelButton
        onClick={toogleMenu}
        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
        alt="Back"
      />
      <InstructionButton onClick={toggleInstructions}>
        <FaQuestionCircle size={24} />
      </InstructionButton>
      <GameFrame
        ref={iframeRef}
        src="/racing/index.html"
        title="Dr1v3n Racing Game"
        sandbox="allow-scripts allow-same-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />

      {/* <TestButton 
        visible={showTestButton} 
        onClick={handleTestClick}
      >
        TEST POPUP
      </TestButton> */}

      {showPopup && !IsPointsReached && (
        <div className="overlay">
          <div className="popup" style={{ bottom: "0px", width: "100%" }}>
            <div className="popup-content">
              <h2 className="epic2">
                {popupType === "win" ? "EPIC WIN!" : "GAME OVER"}
              </h2>

              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="cancel-img"
                onClick={closePopUp}
                alt="Close"
              />
              <div className="row text-center">
                <div className="col-12">
                  <div className="epic-div">
                    <p className="pointsLabel">TOKENS EARNED</p>
                    <div className="pointsValue1">
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                        alt="logo"
                      />
                      <p
                        className="pointsNumber"
                        style={{ marginBottom: "0px" }}
                      >
                        {earnedTokens}
                      </p>
                    </div>
                    {playerStage >= 2 && (
                      <p
                        className="pointsLabel"
                        style={{ fontSize: "14px", marginTop: "5px" }}
                      >
                        (STAGE {playerStage - 1} COMPLETED)
                      </p>
                    )}
                  </div>
                  <p className="bottom-text">
                    Play more to reach higher stages and earn more tokens!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPopup && IsPointsReached && (
        <div className="overlay">
          <div className="popup">
            <div className="popup-content">
              <h2 className="epic2">{"GAME OVER"}</h2>

              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="cancel-img"
                onClick={closePopUp}
                alt="Close"
              />
              <div className="row text-center">
                <div className="col-12">
                  <div className="epic-div">
                    <p
                      className="pointsLabel"
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        margin: "20px 0",
                        textAlign: "center",
                      }}
                    >
                      {popupType === "win" ? "YOU WON!" : "YOU LOST!"}
                    </p>
                  </div>
                  <p className="bottom-text">
                    Great job! 🏎️ You've reached your points limit — try again
                    later!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInstructions && (
        <div className="overlay">
          <div className="popup">
            <div className="popup-content">
              <h2 className="epic2">HOW TO PLAY</h2>

              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="cancel-img"
                onClick={toggleInstructions}
                alt="Close"
              />
              <div className="row text-center">
                <div className="col-12">
                  <div className="instructions-content">
                    <ol
                      className="instruction-list"
                      style={{ fontSize: "0.9rem", textAlign: "left" }}
                    >
                      <li>DRIVE YOUR CAR USING ARROW KEYS OR TOUCH CONTROLS</li>
                      <li>AVOID OBSTACLES AND STAY ON THE TRACK</li>
                      <li>COMPLETE STAGES TO COLLECT TOKENS</li>
                      <li>TAP THE CENTER OF SCREEN TO PAUSE/RESUME</li>
                    </ol>
                    <div className="token-info">
                      <h5>TOKEN REWARDS:</h5>
                      <ul
                        className="reward-list"
                        style={{ fontSize: "0.9rem", textAlign: "left" }}
                      >
                        <li>ON COMPLETING STAGE 1: GET 500 $MEMETV TOKENS</li>
                        <li>ON COMPLETING STAGE 2: GET 1000 $MEMETV TOKENS</li>
                        <li>AND SO ON... (500 $MEMETV TOKENS PER STAGE)</li>
                        <li>UP TO 4500 $MEMETV TOKENS AT STAGE 10..!</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </RacingContainer>
  );
};

export default Racing;
